import { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { FormCreator, FormFieldDef } from "@/components/shared/form-creator";
import {
  fetchVendorParentCategories,
  fetchVendorChildServices,
  fetchVendorProperties,
  fetchUnitsForProperty,
  createVendorAppointment,
  createVendorUnit,
} from "../appointments.api";
import { PropertyUnit } from "@/models/properties.model";
import { uuid } from "@/interfaces";
import UnitCreationModal from "./UnitCreationModal";
import { bookingRefusalMessage } from "@/lib/appointment-booking";
import { calendarDay } from "@/lib/appointment-reschedule";

const bookingSchema = z.object({
  parentService: z.string().min(1, "Service category is required"),
  childService: z.string().min(1, "Sub-service is required"),
  property: z.string().min(1, "Property is required"),
  unit: z.string().min(1, "Unit is required"),
  date: z.date({ required_error: "Date is required" }),
  work_notes: z.string().optional(),
});

type BookingFormValues = z.infer<typeof bookingSchema>;

export default function BookingSheet({
  sheetOpen,
  closeSheet,
  onAppointmentCreated,
}: {
  sheetOpen: boolean;
  closeSheet: () => void;
  onAppointmentCreated?: () => void;
}) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Options state
  const [parentChoices, setParentChoices] = useState<string[]>([]);
  const [childList, setChildList] = useState<{ id: string; name: string }[]>([]);
  const [properties, setProperties] = useState<{ id: string; name: string }[]>([]);
  const [units, setUnits] = useState<{ id: string; unit_number: string }[]>([]);

  // Unit creation modal
  const [unitModalOpen, setUnitModalOpen] = useState(false);

  const form = useForm<BookingFormValues>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      parentService: "",
      childService: "",
      property: "",
      unit: "",
      date: undefined,
      work_notes: "",
    },
  });

  const watchedValues = form.watch();

  // Fetch initial data when sheet opens
  useEffect(() => {
    if (!sheetOpen) return;
    fetchVendorParentCategories().then(setParentChoices).catch(console.error);
    fetchVendorProperties().then(setProperties).catch(console.error);
  }, [sheetOpen]);

  // Fetch child services when parent changes
  useEffect(() => {
    form.setValue("childService", "");
    setChildList([]);
    if (!watchedValues.parentService) return;
    fetchVendorChildServices(watchedValues.parentService).then(setChildList).catch(console.error);
  }, [watchedValues.parentService]);

  // Fetch units when property changes
  useEffect(() => {
    form.setValue("unit", "");
    setUnits([]);
    if (!watchedValues.property) return;
    fetchUnitsForProperty(watchedValues.property as uuid).then(setUnits).catch(console.error);
  }, [watchedValues.property]);

  const getTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  };

  const resetAll = () => {
    setStep(1);
    form.reset();
    setChildList([]);
    setUnits([]);
  };

  const handleUnitCreated = async (unitData: Omit<PropertyUnit, "id" | "created_at" | "updated_at" | "deleted_at">) => {
    try {
      const created = await createVendorUnit(unitData);
      toast.success("Unit created successfully");
      setUnitModalOpen(false);
      if (watchedValues.property) {
        setUnits(await fetchUnitsForProperty(watchedValues.property as uuid));
        form.setValue("unit", created.id);
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to create unit");
    }
  };

  const confirm = async (closeAfter: boolean) => {
    const isValid = await form.trigger();
    if (!isValid) {
      toast.error("Please fill in all required fields");
      return;
    }

    const values = form.getValues();
    const payload = {
      property: values.property as uuid,
      unit: values.unit as uuid,
      service: values.childService as uuid,
      date: calendarDay(values.date),
      work_order_notes: values.work_notes || undefined,
    };

    setSaving(true);
    try {
      await createVendorAppointment(payload);
      toast.success("Appointment booked");
      if (closeAfter) {
        closeSheet();
        resetAll();
      } else {
        resetAll();
      }
      onAppointmentCreated?.();
    } catch (error) {
      toast.error(bookingRefusalMessage(error));
    } finally {
      setSaving(false);
    }
  };

  // Form definition with dynamic options
  const formDefinition: FormFieldDef[] = useMemo(
    () => [
      {
        type: "section",
        name: "service-section",
        label: "Service Details",
      },
      {
        type: "combobox",
        name: "parentService",
        label: "",
        placeholder: "Service category",
        options: parentChoices.map((p) => ({ label: p, value: p })),
        required: true,
      },
      {
        type: "combobox",
        name: "childService",
        label: "",
        placeholder: "Sub-service",
        options: childList.map((s) => ({ label: s.name, value: s.id })),
        disabledWhen: (values) => !values.parentService,
        required: true,
      },
      {
        type: "section",
        name: "property-section",
        label: "Property Details",
      },
      {
        type: "combobox",
        name: "property",
        label: "",
        placeholder: "Property",
        options: properties.map((p) => ({ label: p.name, value: p.id })),
        required: true,
      },
      {
        type: "combobox",
        name: "unit",
        label: "",
        placeholder: "Unit",
        options: units.map((u) => ({ label: u.unit_number, value: u.id })),
        disabledWhen: (values) => !values.property,
        required: true,
        addonRight: (values: any) => (
          <Button
            type="button"
            variant="outline"
            onClick={() => setUnitModalOpen(true)}
            disabled={!values.property}
          >
            Add Unit
          </Button>
        ),
      },
      {
        type: "date",
        name: "date",
        label: "",
        placeholder: "Pick a date",
        minDate: getTomorrow,
        required: true,
      },
      {
        type: "textarea",
        name: "work_notes",
        label: "",
        placeholder: "Work Notes (optional)",
      },
    ],
    [parentChoices, childList, properties, units]
  );

  const canProceed =
    watchedValues.parentService &&
    watchedValues.childService &&
    watchedValues.property &&
    watchedValues.unit &&
    watchedValues.date;

  return (
    <>
      <Sheet
        open={sheetOpen}
        onOpenChange={() => {
          closeSheet();
          resetAll();
        }}
      >
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>New Appointment</SheetTitle>
            <SheetDescription>
              {step === 1 && "Select service, property, unit and date"}
              {step === 2 && "Review then save"}
            </SheetDescription>
          </SheetHeader>

          <div className="p-4 space-y-6">
            {step === 1 && (
              <FormCreator
                form={form}
                onSubmit={() => setStep(2)}
                formDefinition={formDefinition}
                formClassName="space-y-4"
                renderButtons={() => (
                  <div className="flex justify-end gap-4 pt-4">
                    <Button disabled={!canProceed} onClick={() => setStep(2)}>
                      Next
                    </Button>
                  </div>
                )}
              />
            )}

            {step === 2 && (
              <>
                <ul className="space-y-1 text-sm">
                  <li>
                    <strong>Service Category:</strong> {watchedValues.parentService}
                  </li>
                  <li>
                    <strong>Sub-service:</strong> {childList.find((c) => c.id === watchedValues.childService)?.name}
                  </li>
                  <li>
                    <strong>Property:</strong> {properties.find((p) => p.id === watchedValues.property)?.name}
                  </li>
                  <li>
                    <strong>Unit:</strong> {units.find((u) => u.id === watchedValues.unit)?.unit_number}
                  </li>
                  <li>
                    <strong>Date:</strong> {watchedValues.date ? format(watchedValues.date, "PPP") : "—"}
                  </li>
                  <li>
                    <strong>Work Notes:</strong> {watchedValues.work_notes || "—"}
                  </li>
                </ul>

                <div className="flex justify-end gap-4 pt-4">
                  <Button variant="outline" onClick={() => setStep(1)} disabled={saving}>
                    Back
                  </Button>
                  <Button variant="secondary" onClick={() => confirm(false)} disabled={saving}>
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save & Book Another
                  </Button>
                  <Button onClick={() => confirm(true)} disabled={saving}>
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save & Close
                  </Button>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <UnitCreationModal
        open={unitModalOpen}
        onOpenChange={setUnitModalOpen}
        propertyId={watchedValues.property as uuid}
        onSubmit={handleUnitCreated}
      />
    </>
  );
}
